import { useEffect, useRef } from 'react';

const TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || '';

/**
 * Build the marker DOM.
 *
 * The structure is important: the OUTER element is what Mapbox sets its
 * positioning transform on, so it MUST be a simple fixed-size box with no
 * padding, no border, no children that alter its layout, and definitely no
 * CSS animations on `transform`. All the decorative pieces (dot, pulse ring)
 * are children positioned absolutely inside the outer wrapper — they can
 * animate freely without fighting Mapbox's positioning.
 */
function makeMarkerEl() {
  const el = document.createElement('div');
  el.className = 'live-game-marker';
  el.innerHTML =
    '<span class="live-game-marker-ring"></span>' +
    '<span class="live-game-marker-ring live-game-marker-ring-2"></span>' +
    '<span class="live-game-marker-dot"></span>';
  return el;
}

export default function Map({
  center = [-95, 30],
  zoom = 1.4,
  markers = [],
  style,
  interactive = true,
}) {
  const resolvedStyle =
    style ||
    (getComputedStyle(document.documentElement)
      .getPropertyValue('--map-style')
      .trim() === 'dark'
      ? 'mapbox://styles/mapbox/dark-v11'
      : // Streets has more visual texture (roads, parks, labels) than
        // light-v11 which was almost blank. Still light-toned so it works
        // with the cream Clubhouse theme.
        'mapbox://styles/mapbox/streets-v12');
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  // Initialize map once.
  useEffect(() => {
    if (!containerRef.current) return;
    if (!TOKEN) return;

    let cancelled = false;
    let map;

    import('mapbox-gl').then((mod) => {
      if (cancelled || !containerRef.current) return;
      const mapboxgl = mod.default || mod;
      mapboxgl.accessToken = TOKEN;
      map = new mapboxgl.Map({
        container: containerRef.current,
        style: resolvedStyle,
        center,
        zoom,
        interactive,
        attributionControl: false,
        logoPosition: 'bottom-left',
        // Flat Mercator instead of the default 3D globe. The globe is
        // disorienting for fat-thumb / drunk navigation — mercator is
        // easier to pan and pinch-zoom without accidentally rotating.
        projection: 'mercator',
        // Lock the camera so drunk users can't tilt the map into an
        // unreadable 3D angle by pinching with two fingers.
        pitchWithRotate: false,
        dragRotate: false,
        touchZoomRotate: true, // allow pinch-zoom but…
        // Cosmetic: fade labels in/out on zoom, keeps roads soft.
        fadeDuration: 300,
      });

      // …disable the rotation component of the pinch gesture so users
      // can't accidentally spin the map while zooming.
      map.touchZoomRotate?.disableRotation?.();
      map.touchPitch?.disable?.();
      mapRef.current = { mapboxgl, map };
    });

    return () => {
      cancelled = true;
      if (mapRef.current?.map) {
        mapRef.current.map.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync markers when they change.
  useEffect(() => {
    const ctx = mapRef.current;
    if (!ctx) {
      // Map not ready yet — try again next tick.
      const id = setInterval(() => {
        if (mapRef.current) {
          syncMarkers();
          clearInterval(id);
        }
      }, 100);
      return () => clearInterval(id);
    }
    syncMarkers();

    function syncMarkers() {
      const { mapboxgl, map } = mapRef.current;
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      markers.forEach((m) => {
        const el = makeMarkerEl();
        const marker = new mapboxgl.Marker({
          element: el,
          // Pin the marker's geometric center exactly on the lngLat so
          // there's no anchor drift as the map pans/zooms.
          anchor: 'center',
          // Let rotations ignore the marker — prevents subpixel shifts
          // when Mapbox re-projects during pitch/bearing changes.
          rotationAlignment: 'viewport',
          pitchAlignment: 'viewport',
        })
          .setLngLat(m.lngLat)
          .addTo(map);
        markersRef.current.push(marker);
      });
    }
  }, [markers]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      {!TOKEN && (
        <div
          style={{
            height: '100%',
            background:
              'radial-gradient(ellipse at center, var(--accent-soft), transparent 60%), var(--surface-alt)',
          }}
        />
      )}
    </div>
  );
}
